import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";

import {
    BulkResponse,
    BulkUtil,
    RequestUtil,
    SearchResponse
} from "@shopliftr/common-js/shared";

import {
    IReadOnlyInternalApiClientService,
    InternalApiRequestService,
    ListUtil,
    IQueryInternalApiClientService
} from "@shopliftr/common-ng";

import {
    ISearchSort, ModelAction
} from "@shopliftr/common-js/data";

import {
    PlacementSettings, PlacementSettingsSearchFilterOptions
} from "@shopliftr/common-js/smart";

/**
 * HTTP Request Service for managing placement settings via Internal API.
 *
 * This service implements the **Repository Pattern** (API Client Service), providing typed
 * HTTP operations for PlacementSettings resources. It encapsulates the API endpoint path,
 * builds typed requests using RequestUtil, and maps responses to domain models.
 *
 * @pattern Repository Pattern / HTTP Request Service
 * @providedIn root
 * @implements {IReadOnlyInternalApiClientService<PlacementSettings>}
 * @implements {IQueryInternalApiClientService<PlacementSettings>}
 *
 * @see {@link IReadOnlyInternalApiClientService}
 * @see {@link IQueryInternalApiClientService}
 * @see {@link PlacementSettings}
 */
@Injectable({ providedIn: "root" })
export class PlacementSettingsApiClientService implements IReadOnlyInternalApiClientService<PlacementSettings>,
    IQueryInternalApiClientService<PlacementSettings> {
    /**
     * Base API endpoint path for placement settings operations.
     * Used to construct full URLs for HTTP requests.
     */
    readonly basePath = "placement-settings";

    /**
     * Internal API request service for making HTTP calls to the backend.
     * Handles request preprocessing, URL generation, and error handling.
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves placement settings for a specific campaign by ID.
     *
     * @param id - The unique identifier of the campaign
     * @returns Observable that emits the PlacementSettings for the campaign
     *
     * @example
     * ```typescript
     * this.placementSettingsService.get$('campaign-123').subscribe({
     *   next: (settings) => console.log('Settings:', settings),
     *   error: (err) => console.error('Failed to load settings:', err)
     * });
     * ```
     */
    get$(id: string): Observable<PlacementSettings> {
        const request = RequestUtil.createGetRequest<void>(id, PlacementSettings.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$<PlacementSettings>(request, this.basePath).pipe(map((response) => {
            return response.payload;
        }));
    }

    /**
     * Retrieves all PlacementSettings entities matching the specified filter criteria.
     * Results are returned as a complete array without pagination.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [sort] - Optional sorting configuration
     * @returns Observable that emits an array of all matching PlacementSettings entities
     *
     * @example
     * ```typescript
     * this.placementSettingsService.getAll$(filterOptions).subscribe(settings => {
     *   console.log(`Found ${settings.length} placement settings`);
     * });
     * ```
     */
    getAll$(filterOptions: PlacementSettingsSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<PlacementSettings>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, PlacementSettings.modelName, undefined, undefined, filterOptions, sort
        );
        return this._internalApiRequestService.getAll$<PlacementSettings, PlacementSettingsSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Performs a paginated search for PlacementSettings entities matching the specified criteria.
     * Returns results with pagination metadata for efficient data loading.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [page] - Zero-based page number for pagination
     * @param [pageSize] - Number of results per page
     * @param [sort] - Optional sorting configuration
     * @returns Observable that emits a SearchResponse containing matching entities and pagination metadata
     *
     * @example
     * ```typescript
     * this.placementSettingsService.search$(filterOptions, 0, 20).subscribe(response => {
     *   console.log(`Page ${response.page} of ${response.totalPages}`);
     * });
     * ```
     */
    search$(
        filterOptions: PlacementSettingsSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<PlacementSettings>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            PlacementSettings.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$<PlacementSettings, PlacementSettingsSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Retrieves multiple PlacementSettings entities in a single bulk request.
     * More efficient than making individual get() calls when fetching multiple entities.
     *
     * @param ids - Array of unique identifiers for the PlacementSettings to retrieve
     * @returns Observable that emits a BulkResponse containing all requested entities
     *
     * @example
     * ```typescript
     * this.placementSettingsService.bulkGet$(['id-1', 'id-2']).subscribe(response => {
     *   console.log('Bulk response:', response);
     * });
     * ```
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<PlacementSettings>(id, PlacementSettings.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
